import React from 'react'
import { ArrowDown, ArrowUp, Ban, BedDouble, CalendarPlus, TrendingDown, TrendingUp } from 'lucide-react'
import GuideLayout from '@/components/help/GuideLayout'
import GuideSection from '@/components/help/GuideSection'
import GuideTip from '@/components/help/GuideTip'
import GuideRelatedLinks from '@/components/help/GuideRelatedLinks'
import type { SharedProps } from '@/types/shared'

/**
 * The Market Intelligence guide (/guides/market-intelligence). Copy follows the locked market
 * vocabulary (docs/market-vocabulary.md): list price, compset, compset median, sold out, minimum
 * stay, checked. Thresholds quoted here are App::Market::Config's; keep the two in step.
 */

function Term({ children }: { children: React.ReactNode }) {
  return <strong className="text-foreground">{children}</strong>
}

/** A legend mark in a fixed-width slot, so every line of text starts at the same edge. */
function Swatch({ className, children }: { className?: string; children?: React.ReactNode }) {
  return (
    <span aria-hidden="true" className="flex h-5 w-5 shrink-0 items-center justify-center">
      {children ?? <span className={`block ${className}`} />}
    </span>
  )
}

const WORTH_A_LOOK: { icon: React.ElementType; title: string; body: string }[] = [
  {
    icon: Ban,
    title: 'Every competitor checked is sold out',
    body: 'At least two competitors reported for the night and every one of them is sold out, while you still have a price on sale.',
  },
  {
    icon: TrendingUp,
    title: 'Competitors are selling out and you are cheaper',
    body: 'At least half of the competitors with data are sold out, and your price is well below the compset median: at least 10% and at least £5 below.',
  },
  {
    icon: BedDouble,
    title: 'You are the cheapest on busy nights',
    body: 'The market is Busy or Peak for that weekday, no competitor is cheaper than you, and the next cheapest hotel is at least 10% and £5 above you.',
  },
  {
    icon: TrendingDown,
    title: 'You are priced above every competitor',
    body: 'Your price is at least 10% and £5 above the most expensive competitor priced that night. The card says how many of your rooms are on the books.',
  },
  {
    icon: CalendarPlus,
    title: 'Competitor prices are up and no event is logged',
    body: 'The compset median is at least 15% and £5 above a typical night for that weekday, and you have no event logged for it. If something is on in town, logging it on Events lets the engine price it too.',
  },
]

export default function MarketIntelligenceGuide({
  currentUser,
  properties,
}: Pick<SharedProps, 'currentUser' | 'properties'>) {
  return (
    <GuideLayout
      currentUser={currentUser}
      properties={properties}
      title="Market Intelligence"
      subtitle="How your price compares with your competitors' list prices on Booking.com, night by night, and what changed since the last check."
    >
      <GuideSection title="What Is Compared">
        <p className="text-sm leading-relaxed text-muted-foreground">
          <Term>Your price</Term> (shown as <Term>Priced at</Term>) is the price the engine is asking for one room, your{' '}
          <Term>comparison room</Term>, on its main rate plan, for two guests. It is one room, never an average of your
          rooms. By default it is the room with the most units; you can choose another on the Compset tab.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          A competitor's <Term>list price</Term> is their cheapest room that fits two guests, for one night, as
          Booking.com shows it to the public: before member and deal discounts, with taxes and fees included. It is not
          what a member or a deal booker pays.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Your <Term>compset</Term> is the hotels you chose to compare with. Your own Booking.com listing is checked the
          same way and shown beside your price for reference, but it is never counted as a competitor and never moves a
          figure. In a combined inventory the same goes for every other listing of the building: one already in your
          compset is marked not counted on the Compset tab, and none can be added as a competitor.
        </p>
      </GuideSection>

      <GuideSection title="Reading the Chart">
        <p className="text-sm leading-relaxed text-muted-foreground">
          <Term>Your position</Term> shows every night the latest check covers, about two months ahead.
        </p>
        <ul className="space-y-2.5 text-sm text-muted-foreground">
          <li className="flex gap-3">
            <Swatch className="h-[3px] w-5 rounded-full bg-primary" />
            <span>
              <Term>Priced at</Term>: the teal line is your price. A gap in it means the engine has no price for that
              night.
            </span>
          </li>
          <li className="flex gap-3">
            <Swatch className="h-3 w-5 rounded-sm bg-slate-300/60 ring-1 ring-inset ring-slate-400/50" />
            <span>
              <Term>Compset range</Term>: the grey band runs from the cheapest to the most expensive competitor priced
              that night.
            </span>
          </li>
          <li className="flex gap-3">
            <Swatch className="w-5 border-t-2 border-dashed border-slate-500" />
            <span>
              <Term>Compset median</Term>: the dashed line is the middle list price. It needs at least two competitors
              priced; with only one, the page shows that hotel's price and names it instead of talking about "the
              market".
            </span>
          </li>
          <li className="flex gap-3">
            <Swatch className="h-3 w-3 rounded-sm bg-slate-100 ring-1 ring-inset ring-slate-300" />
            <span>
              <Term>Weekend</Term>: shaded columns are Saturday and Sunday nights, the same weekend the engine prices.
            </span>
          </li>
          <li className="flex gap-3">
            <Swatch>
              <svg width="12" height="10" viewBox="0 0 8 7">
                <path d="M0 7 L8 7 L4 0 Z" fill="#f59e0b" />
              </svg>
            </Swatch>
            <span>
              <Term>Worth a look</Term>: a triangle under a night means one of the findings below covers it.
            </span>
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Under the chart, four lanes line up with the nights above them:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <Term>Event</Term>: events you have logged on Pricing rules, Events.
          </li>
          <li>
            <Term>Sold out</Term>: one dot per competitor with no room for two that night.
          </li>
          <li>
            <Term>Market</Term>: market demand for the night (see below), taller and warmer as it gets busier.
          </li>
          <li>
            <Term>OTB</Term>: your own rooms on the books for the night. A tall Market bar over a short OTB bar is the
            market filling while you are not.
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Click a night, or use the arrow keys on the lanes, to open it underneath. Page Up and Page Down jump a week.
        </p>
      </GuideSection>

      <GuideSection title="The Night's Lineup">
        <p className="text-sm leading-relaxed text-muted-foreground">
          When you open a night, every hotel sits on one price ruler: you first, then your competitors in compset order.
          Each competitor keeps the same number everywhere on the page, so number 2 in the lineup is number 2 in the
          tables and on the price and reviews map. As you move from night to night the rows stay put and the dots slide,
          so you can watch the market's shape change.
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <Term>Sold out</Term>: the competitor cannot be booked for check-in that night, so it has no price and no
            dot.
          </li>
          <li>
            <Term>Min 2 nights</Term>: the competitor only sells stays of at least that many nights from that check-in
            date. Their price is real, but a one-night guest cannot book it.
          </li>
          <li>
            <span className="inline-flex items-center gap-1">
              <ArrowUp className="h-3.5 w-3.5 text-emerald-600" />
              <ArrowDown className="h-3.5 w-3.5 text-rose-600" />
            </span>{' '}
            <Term>Change since the last check</Term>: a hollow dot marks the price at the previous check, joined by a
            green line when they raised it and a red one when they lowered it. Colour shows direction only: a competitor
            raising their price is not good or bad news on its own.
          </li>
          <li>
            <Term>Not checked</Term>: the competitor was missing from the latest check, so that night has no figure for
            them rather than an older one.
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          The sentence above the lineup sums the night up. <Term>Open prices</Term> takes you to that month on your
          Prices calendar, on the comparison room.
        </p>
      </GuideSection>

      <GuideSection title="Worth a Look">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Runs of nights where the comparison says something worth checking. Each card states the facts with the numbers
          behind them and links to the night. None of them is an instruction: being cheaper than the market can be
          exactly right for your hotel.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Each night appears on one card at most. When two findings cover the same nights, the more specific one leads the
          card and the other is written inside it, naming its own nights, with its link beside the card's own.
        </p>
        <ul className="space-y-3">
          {WORTH_A_LOOK.map(({ icon: Icon, title, body }) => (
            <li key={title} className="flex gap-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
                <Icon className="h-3.5 w-3.5" />
              </span>
              <div>
                <p className="text-sm font-medium text-foreground">{title}</p>
                <p className="mt-0.5 text-sm leading-relaxed text-muted-foreground">{body}</p>
              </div>
            </li>
          ))}
        </ul>
      </GuideSection>

      <GuideSection title="Market Demand">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Each night's compset median is held against a typical one for the same weekday, so a Friday is compared with
          other Fridays:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <Term>Quiet</Term>: more than 10% below a typical night for that weekday.
          </li>
          <li>
            <Term>Normal</Term>: within the usual range.
          </li>
          <li>
            <Term>Busy</Term>: at least 15% above typical.
          </li>
          <li>
            <Term>Peak</Term>: at least 35% above typical.
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          <Term>Market filling up</Term> means at least half of the competitors with data are sold out, with at least
          two reporting. Such a night is always at least Busy. With fewer than two competitors priced there is no market
          to measure, and the night reads <Term>Not enough prices</Term>.
        </p>
      </GuideSection>

      <GuideSection title="Market Moves">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Compares the latest check with the previous day of checks, night by night, competitor by competitor. A price
          change is listed when a competitor's list price for the same night moved by at least 3% and at least £3;
          smaller changes are left out. Nights that sold out or came back on sale are listed too. The first day a
          compset is checked there is nothing to compare yet, and the section says so.
        </p>
      </GuideSection>

      <GuideSection title="When Prices Are Checked">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Competitors' list prices are checked twice a day, at 06:00 and 18:00 London time. The chip at the top of the
          page says when the last check ran. It turns amber when the check is more than 26 hours old or a competitor was
          missed, and About this data at the bottom of the page names who was missed.
        </p>
      </GuideSection>

      <GuideSection title="What This Page Never Does">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Market Intelligence never changes a price and never recommends a number. It shows you where you stand and
          links to your Prices calendar or your Events. Whether the engine itself uses competitor prices is up to the{' '}
          <Term>Market Position</Term> rule; the chip at the top of the page shows whether it is on.
        </p>
      </GuideSection>

      <GuideSection title="Tips">
        <div className="space-y-3">
          <GuideTip type="do">
            Pick competitors a guest would genuinely weigh against you: similar location, similar rooms, similar
            standard. Four to six is plenty.
          </GuideTip>
          <GuideTip type="do">
            Link your own Booking.com listing first, so it is never added as a competitor and distances can be shown.
          </GuideTip>
          <GuideTip type="dont">
            Do not chase every move. One competitor changing a few nights is information, not an instruction; look for
            several hotels moving the same way, or the market filling while you are not.
          </GuideTip>
        </div>
      </GuideSection>

      <GuideRelatedLinks
        links={[
          { slug: 'market-position', title: 'Market Position' },
          { slug: 'rate-calendar', title: 'Rate Calendar' },
          { slug: 'events', title: 'Events' },
        ]}
      />
    </GuideLayout>
  )
}
